import { useNavigate } from 'react-router-dom'
import { Truck } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useShipments, useSites, type ShipmentRow } from '../api/store'
import { DateFilter, FilterBar, SearchFilter, SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { Code, Status, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import { SHIPMENT_KINDS, SHIPMENT_STATUSES, shipmentKindLabel, shipmentStatusLabel } from '../model/shipment'

const LIMIT = 25

/** Shipments — F5.1 · F5.2: trips between sites, what was loaded, unloaded, missing and re-weighed. */
export function ShipmentsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({
    status: '',
    kind: '',
    siteId: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
  })
  const sites = useSites()
  const q = useShipments({
    page: f.page,
    limit: LIMIT,
    status: f.status || undefined,
    kind: f.kind || undefined,
    siteId: f.siteId || undefined,
    search: f.search || undefined,
    dateFrom: f.dateFrom || undefined,
    dateTo: f.dateTo || undefined,
  })

  const columns: Column<ShipmentRow>[] = [
    {
      id: 'code',
      header: 'Trip',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <Code>{r.code}</Code>
          <span className="text-[12px] text-muted">{shipmentKindLabel(r.kind)}</span>
        </span>
      ),
    },
    {
      id: 'route',
      header: 'From → to',
      cell: (r) => (
        <span className="text-[14px]">
          {r.fromLabel} <span className="text-muted">→</span> {r.toLabel}
        </span>
      ),
    },
    { id: 'vehicle', header: 'Vehicle', cell: (r) => <Code>{r.vehicleNo}</Code>, hideBelow: 'lg' },
    { id: 'dispatched', header: 'Left', cell: (r) => <When at={r.dispatchedAt} />, hideBelow: 'md' },
    { id: 'received', header: 'Received', cell: (r) => <When at={r.receivedAt} />, hideBelow: 'lg' },
    { id: 'bags', header: 'Bags', align: 'right', mono: true, cell: (r) => r.bags },
    {
      id: 'unloaded',
      header: 'Unloaded',
      align: 'right',
      mono: true,
      hideBelow: 'sm',
      cell: (r) => (r.status === 'LOADING' || r.status === 'IN_TRANSIT' ? <span className="text-muted">—</span> : r.unloaded),
    },
    {
      id: 'missing',
      header: 'Missing',
      align: 'right',
      mono: true,
      hideBelow: 'sm',
      cell: (r) => (r.missing > 0 ? <span className="font-semibold text-danger">{r.missing}</span> : <span className="text-muted">0</span>),
    },
    { id: 'recheck', header: 'Re-weigh', align: 'right', mono: true, cell: (r) => r.recheck, hideBelow: 'md' },
    { id: 'status', header: 'Status', cell: (r) => <Status status={r.status} label={shipmentStatusLabel(r.status)} /> },
  ]

  return (
    <ConsolePage
      eyebrow="Store"
      title="Shipments"
      description="Vehicle trips between collection points, stores and customers: loading, on the road, received, short."
    >
      <DataTable<ShipmentRow>
        caption="Shipments"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/shipments/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Trip, challan or vehicle" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. RJ27GA1234" />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={SHIPMENT_STATUSES.map((s) => ({ value: s, label: shipmentStatusLabel(s) }))}
            />
            <SelectFilter label="Kind" value={f.kind} onChange={(kind) => setF({ kind })} options={SHIPMENT_KINDS.map((k) => ({ value: k, label: shipmentKindLabel(k) }))} />
            <SelectFilter
              label="Site (from or to)"
              value={f.siteId}
              onChange={(siteId) => setF({ siteId })}
              options={(sites.data ?? []).map((s) => ({ value: s.id, label: s.name || s.code }))}
            />
            <DateFilter label="Left from" value={f.dateFrom} onChange={(dateFrom) => setF({ dateFrom })} />
            <DateFilter label="Left until" value={f.dateTo} onChange={(dateTo) => setF({ dateTo })} />
          </FilterBar>
        }
        empty={<EmptyState icon={<Truck />} title="No trips match" description="Trips appear when a vehicle starts loading at a collection point or store." />}
      />
    </ConsolePage>
  )
}
